import { DistanceMarkers } from "common/Map/DistanceMarkers";
import { Marker } from "common/Map/Marker";
import { Polyline } from "common/Map/Polyline";
import _ from "lodash";
import p from "prop-types";
import React from "react";

export const sanitizeTrackPoints = (trackPoints, slice) => {
  const { sanitizedPoints, indexMapping } = getSanitizedTrackPointsWithMapping(trackPoints);
  if (!slice) {
    return sanitizedPoints;
  }
  const sliceStart = indexMapping[slice[0]];
  const sliceEnd = indexMapping[slice[1]];
  const newSlice = sanitizedPoints.slice(sliceStart, sliceEnd + 1);
  return newSlice;
};

// filter out route trackPoints that are missing a lat/lng,
// and return the filtered points along with an array used to
// lookup the index of a sanitized point from the index of a
// non-sanitized point in O(1) time
export const getSanitizedTrackPointsWithMapping = _.memoize((trackPoints) => {
  const indexMapping = Array(trackPoints.length);
  const sanitizedPoints = Array(trackPoints.length);
  let sanitizedIndex = 0;
  for (let i = 0; i < trackPoints.length; i++) {
    const routePoint = trackPoints[i];
    if (Number.isFinite(routePoint.lat) && Number.isFinite(routePoint.lng)) {
      sanitizedPoints[sanitizedIndex] = routePoint;
      indexMapping[i] = sanitizedIndex;
      sanitizedIndex++;
    } else {
      indexMapping[i] = sanitizedIndex;
    }
  }
  sanitizedPoints.length = sanitizedIndex;
  console.log(
    `getSanitizedTrackPointsWithMapping cache miss for a ${trackPoints.length}-point trackPoint array (${
      trackPoints.length - sanitizedPoints.length
    } points filtered)`
  );

  return { sanitizedPoints, indexMapping };
});

// const DEFAULT_COLOR = 16711680
const DEFAULT_COLOR_CSS = "#FF0000";
const EXCLUDED_POINT_COLOR = "#A0A0A0";

// this double memoize thing allows us to invalidate the cache when trackPoints change OR when slice change
const splitOnColor = _.memoize((sanitizedPoints) =>
  _.memoize(
    (slice) => _splitOnColor(sanitizedPoints, slice),
    (slice) => slice && `${slice[0]}:${slice[1]}`
  )
);
const _splitOnColor = (sanitizedPoints, slice) => {
  let lineSegments = [];
  let currentSegment = [];
  let currentColor = sanitizedPoints[sanitizedPoints.length - 1]?.color;
  let excludeSegment = false;

  // do it backwards, because it makes more sense that way
  for (let i = sanitizedPoints.length - 1; i >= 0; i--) {
    const point = sanitizedPoints[i];
    currentSegment.push(point);
    if (point.excludeFromMetrics && !excludeSegment) {
      lineSegments.push(currentSegment);
      currentSegment = [point];
      excludeSegment = true;
      continue;
    }

    if (excludeSegment && !point.excludeFromMetrics) {
      lineSegments.push(currentSegment);
      point.color && (currentColor = point.color);
      currentSegment = [{ ...point, color: currentColor }];
      excludeSegment = false;
      continue;
    }

    if (point.color !== null) {
      if (point.color !== currentColor) {
        lineSegments.push(currentSegment);
        currentSegment = [point]; // include point in next segment to prevent overlap
      }
      currentColor = point.color;
    }
  }
  // clear last segment
  currentSegment.length > 0 && lineSegments.push(currentSegment);
  lineSegments.reverse(); // crappy inplace API
  return lineSegments;
};

const getColorForSegment = (s) => {
  if (s[0].excludeFromMetrics) {
    return EXCLUDED_POINT_COLOR;
  }
  return Number.isFinite(s[0].color) ? cssColor(s[0].color) : DEFAULT_COLOR_CSS;
};

const cssColor = (int) => {
  var color = int < 0 ? "0" : int.toString(16);
  while (color.length < 6) color = "0" + color;
  return "#" + color.toUpperCase();
};

export class TrackLine extends React.PureComponent {
  static defaultProps = {
    color: null,
    showStartMarker: true,
    showEndMarker: true,
    showDistanceMarkers: false,
    weight: 3,
  };

  static propTypes = {
    trackPoints: p.array.isRequired,
  };

  render() {
    const {
      trackPoints,
      color,
      weight,
      slice,
      showStartMarker,
      showEndMarker,
      showDistanceMarkers,
      showSurface,
      onMouseEnter,
      onMouseLeave,
      onClick,
    } = this.props;

    const points = sanitizeTrackPoints(trackPoints, slice);
    // skip color step if we're forcing a specific color for the whole line
    const segments = color ? [points] : splitOnColor(points)(slice);
    return (
      <>
        {segments.map((s, i) => {
          if (s[0]) {
            return (
              <Polyline
                key={s[0].color + "-" + i}
                positions={s}
                weight={weight}
                onClick={onClick}
                showSurface={showSurface}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                color={color || getColorForSegment(s)}
              />
            );
          }
        })}
        {showDistanceMarkers && <DistanceMarkers trackPoints={points} />}
        {showStartMarker && <Marker icon="start" position={points[0]} />}
        {showEndMarker && <Marker icon="stop" position={points[points.length - 1]} />}
      </>
    );
  }
}
